import React, {useEffect, useRef} from 'react';
import {APIProvider, ControlPosition, Map as GoogleMap, useMap, useMapsLibrary} from "@vis.gl/react-google-maps";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import CorporationService from "../../../../services/web/corporation-service.js";
import {useSpiderfy} from "../../../../components/map-report/spiderfy-marker.jsx";
import projectUtils from "../../../../utils/project-utils.js";

/* global google */

const OVERLAP_DISTANCE = 20;

const contentInfoWindow = (event) => `
    <div id="infowindow" style="overflow: hidden">
        <div class="col-md-12">
            <div class="row">
                <div class="col-md-12">
                    <h4 class="m-b-xs">
                        Detalhamento
                    </h4>

                    <p>Câmera / Entidade: <strong>${event.cameraName ?? ""}</strong></p>
                    <p>Nome: <strong>${event?.individualName ?? ""}</strong></p>
                    <p>Data/Hora: <strong>${projectUtils.formatDate(event.eventDateTime, "DD/MM/YYYY hh:mm").replace(",", "")}</strong></p>
                    <p>Latitude: <strong>${event.latitude} | Longitude: ${event.longitude}</strong></p>
                </div>
            </div>
        </div>
    </div>
`;

const MapContent = ({events}) => {
    const map = useMap();
    const mapsLib = useMapsLibrary("maps");

    const {spiderfy, unspiderfy, spiderfiedRef} = useSpiderfy(map);

    const routeRef = useRef(null);
    const markersRef = useRef([]);
    const originalPosRef = useRef(new Map());
    const infoWindowRef = useRef(null);

    useEffect(() => {
        if (!map || !mapsLib) return;

        const infoWindow = new google.maps.InfoWindow();
        infoWindowRef.current = infoWindow;

        const route = new google.maps.Polyline({
            geodesic: true,
            editable: false,
            strokeColor: "#1a73e8",
            strokeOpacity: 0.5,
            strokeWeight: 2,
        });
        routeRef.current = route;

        const bounds = new google.maps.LatLngBounds();
        const eventsWithLatLng = events?.filter(e => e.latitude && e.longitude) ?? [];

        const routeData = eventsWithLatLng.map(({latitude, longitude}) => {
            bounds.extend(new google.maps.LatLng(latitude, longitude));
            return {lat: latitude, lng: longitude};
        });

        route.setPath(routeData);
        if (!bounds.isEmpty()) map.fitBounds(bounds);
        route.setMap(map);

        eventsWithLatLng.forEach((e, index) => {
            const marker = new google.maps.Marker({position: {lat: e.latitude, lng: e.longitude}});
            marker.id = index;
            marker.setMap(map);

            markersRef.current.push(marker);
            originalPosRef.current.set(index, marker.getPosition());

            marker.addListener("click", () => {
                const projection = map.getProjection();
                if (!projection) return;

                const group = markersRef.current.filter(other => {
                    const p1 = projection.fromLatLngToPoint(other.getPosition());
                    const p2 = projection.fromLatLngToPoint(marker.getPosition());

                    const scale = Math.pow(2, map.getZoom());
                    const dx = (p1.x - p2.x) * scale;
                    const dy = (p1.y - p2.y) * scale;

                    return Math.sqrt(dx * dx + dy * dy) < OVERLAP_DISTANCE;
                });

                if (group.length > 1 && !spiderfiedRef.current) {
                    spiderfy(group);
                    return;
                }

                infoWindow.close();
                infoWindow.setContent(contentInfoWindow(e));
                infoWindow.open(map, marker);
            });
        });

        const clickListener = map.addListener("click", () => {
            infoWindow.close();
            if (spiderfiedRef.current) unspiderfy(markersRef, originalPosRef);
        });

        return () => {
            clickListener.remove();
            markersRef.current.forEach(marker => marker.setMap(null));
            markersRef.current = [];
            originalPosRef.current.clear();
            route.setMap(null);
            infoWindow.close();
        };
    }, [map, mapsLib, events]);

    return null;
};

const IndividualPassageSearchMap = ({events, show, setShow}) => {
    const {data: corporation} = useQuery({
        queryFn: () => CorporationService.entity(),
        queryKey: ["corporation"]
    });

    return (
        <Modal open={show} setOpen={setShow} title={"Mapa"} className={"w-11/12"}>
            <Modal.Body>
                <APIProvider apiKey={corporation?.maps_api_key ?? ""} language="pt-BR" libraries={["geometry"]}>
                    <GoogleMap
                        defaultZoom={18}
                        defaultCenter={{lat: -23.0, lng: -46.0}}
                        streetViewControl={false}
                        gestureHandling={"greedy"}
                        renderingType="RASTER"
                        style={{width: "100%", height: "calc(100vh - 225px)"}}
                        mapTypeControlOptions={{position: ControlPosition.TOP_RIGHT}}
                    >
                        <MapContent events={events}/>
                    </GoogleMap>
                </APIProvider>
            </Modal.Body>
        </Modal>
    );
};

export default IndividualPassageSearchMap;
